import { useState } from 'react';
import { apiCreateCreditTopUpCheckout } from '~/lib/api/billing';
import { AccessibleModal } from '~/components/account/AccessibleModal';
import { serverMessage } from '~/components/account/PlanCheckoutModal';
import { formatCredits } from '~/lib/plans-model';

function fmtMoney(amount: number, currency = 'usd'): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-2">
      <span className="text-[12px] text-muted">{label}</span>
      <span className="text-right text-[12.5px] text-primary">{value}</span>
    </div>
  );
}

interface TopUpCheckoutConfirmModalProps {
  amount: number;
  credits: number;
  /**
   * Starts the hosted checkout. Defaults to the website top-up (grandfathered
   * website plans); Plus/Pro pass the plan-priced top-up from ~/lib/api/plans.
   */
  startCheckout?: (amount: number) => Promise<{ url: string }>;
  onClose: () => void;
}

/**
 * Redirect-based top-up confirmation. Used when the active payment provider
 * hosts its own checkout (e.g. Dodo): the user reviews the purchase here, then
 * "Continue to payment" hands off to the provider's secure checkout page.
 * Credits are granted server-side by the verified webhook after payment.
 */
export function TopUpCheckoutConfirmModal({
  amount,
  credits,
  startCheckout = apiCreateCreditTopUpCheckout,
  onClose,
}: TopUpCheckoutConfirmModalProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function continueToPayment() {
    setBusy(true);
    setError(null);

    try {
      const { url } = await startCheckout(amount);
      window.location.href = url;
    } catch (err) {
      // the server's reason when it refuses (e.g. top-ups need a plan on this product)
      setError(serverMessage(err, 'Could not start checkout. Please try again.'));
      setBusy(false);
    }
  }

  return (
    <AccessibleModal
      title="Buy credits"
      description="Review your purchase. You'll complete payment on our secure checkout page."
      maxWidth={460}
      dismissible={!busy}
      onClose={onClose}
    >
      {/* Icon + title */}
      <div className="mb-4 flex items-start gap-3.5">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] border border-accent/20 bg-accent/10">
          <span className="i-ph:lightning-fill text-[17px] text-accent" />
        </div>
        <div className="min-w-0 flex-1 pt-1">
          <h3 className="ui-serif m-0 mb-1 text-[20px] tracking-[-0.01em] text-primary">Buy credits</h3>
          <p className="m-0 text-[12px] leading-normal text-muted">
            Review your purchase below. Nothing is charged until you continue.
          </p>
        </div>
      </div>

      {/* Purchase details */}
      <div className="rounded-[10px] border border-hairline bg-raised px-3.5 py-1">
        <Row label="Amount" value={fmtMoney(amount)} />
        <Row
          label="Credits you'll receive"
          value={
            <span>
              <span className="ui-mono text-accent">+{formatCredits(credits)}</span> credits
            </span>
          }
        />
      </div>

      {/* Total — hero panel */}
      <div className="mt-2.5 flex items-baseline justify-between rounded-[10px] border border-accent/20 bg-accent/[0.04] px-3.5 py-3">
        <span className="text-[13px] font-medium text-primary">Total due today</span>
        <span className="ui-serif text-[24px] tracking-[-0.015em] text-accent">{fmtMoney(amount)}</span>
      </div>

      {/* Reassurance */}
      <div className="mt-3 flex items-start gap-2 rounded-[10px] border border-hairline bg-white/[0.02] px-3.5 py-3 text-[11.5px] leading-[1.55] text-secondary">
        <span className="i-ph:lock-simple mt-px flex-shrink-0 text-[14px] text-accent" />
        <span>
          When you continue, you'll be taken to our secure checkout to complete payment. Credits are added to your
          balance right after the payment is confirmed, and never expire.
        </span>
      </div>

      {error && (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-danger/30 bg-danger/[0.07] px-3 py-2.5 text-[12.5px] leading-normal text-danger">
          <span className="i-ph:warning-circle mt-px flex-shrink-0 text-[14px]" />
          {error}
        </div>
      )}

      {/* Actions */}
      <div className="mt-4.5 flex justify-end gap-2 border-t border-hairline pt-3.5">
        <button type="button" onClick={onClose} disabled={busy} className="ui-btn-ghost">
          Cancel
        </button>
        <button type="button" onClick={continueToPayment} disabled={busy} className="ui-btn-accent">
          {busy && <span className="i-ph:spinner-gap animate-spin text-[13px]" />}
          {busy ? 'Redirecting…' : 'Continue to payment'}
        </button>
      </div>
    </AccessibleModal>
  );
}
